// src/lib/tokenRefresh.ts
// Silent JWT refresh interceptor.
// Decodes the JWT exp claim, and when < 30 min remain,
// calls POST /auth/refresh to get a fresh token in background.

import { apiUrl } from '@/config';

const REFRESH_MARGIN_MS = 30 * 60 * 1000; // 30 minutes
let _refreshInflight: Promise<string> | null = null;

/** Decode the `exp` claim from a JWT without any library. */
function getExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Call this before any authenticated request.
 * - If the token has > 30 min left → returns immediately (no-op).
 * - If < 30 min left → silently refreshes via POST /auth/refresh.
 * - Uses single-flight to avoid concurrent refresh calls.
 * - On failure it does NOT kick the user out — the existing token
 *   keeps working until it actually expires and a 401 triggers logout.
 */
export async function ensureFreshToken(): Promise<void> {
  const token = localStorage.getItem('auth_token');
  if (!token) return; // no session → nothing to refresh

  const exp = getExpMs(token);
  if (!exp) return; // can't decode → skip
  if (Date.now() < exp - REFRESH_MARGIN_MS) return; // still fresh

  // Already refreshing? piggyback
  if (_refreshInflight) {
    await _refreshInflight.catch(() => {});
    return;
  }

  _refreshInflight = _doRefresh(token);
  try {
    await _refreshInflight;
  } catch {
    // Swallow — the user keeps their current (still valid) token
  } finally {
    _refreshInflight = null;
  }
}

async function _doRefresh(currentToken: string): Promise<string> {
  const res = await fetch(apiUrl('/auth/refresh'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
  });

  if (!res.ok) {
    console.warn(`Token refresh failed (${res.status}) — will retry on next request`);
    throw new Error(`Refresh failed ${res.status}`);
  }

  const data = await res.json();
  const newToken: string = data.access_token || data.token;
  if (!newToken) throw new Error('No token in refresh response');

  localStorage.setItem('auth_token', newToken);
  return newToken;
}
